import { BOOLEAN_AS_STRING, OrderApiData } from "@swiggy-private/payment-types";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { makeApiCall } from "@payment-client/utils/Api";
import { GiftDetails } from "@payment-client/interfaces/SwiggyMoney";
import { PlaceOrderData } from "@swiggy-private/checkout-core";

export const ENDPOINT = {
    PLACE_ORDER: "/api/v1/order/place",
    CONFIRM_ORDER: "/api/v1/order/confirm",
    GIFTING_PLACE_ORDER: "/api/v3/gifting-order/place",
    GIFTING_CONFIRM_ORDER: "/api/v3/gifting-order/confirm",
};

export interface PlaceOrderParams {
    addressId: string | number;
    paymentMethod: string;
    orderType?: string;
    orderComments?: string;
    proceedWithDuplicateOrder?: boolean;
    useJuspayNative?: boolean;
    [key: string]: unknown;
    // TODO - Cleanup required after feature
    giftDetails?: GiftDetails;
    cbccfdApplicationNumber?: string;
}

export interface ConfirmOrderParams {
    transactionId: string;
    orderId: string | number;
    giftDetails?: GiftDetails;
    [key: string]: unknown;
}

export async function placeOrder(
    apiHost: string,
    params: PlaceOrderParams,
    _?: PlaceOrderData,
    _isConfirmOnAppLaunchEnabled?: boolean,
): Promise<ApiResponse<OrderApiData>> {
    if (__DEV__) {
        const mockPlaceOrder = document.cookie.indexOf("mock_place_order=1") !== -1;
        if (mockPlaceOrder) {
            return new Promise((resolve) => {
                console.log(params);
                const mockResponse: ApiResponse<OrderApiData> = {
                    statusCode: 0,
                    data: {
                        orderData: {
                            orderPaymentMethod: params.paymentMethod,
                            isSwiggyBlackOtpApplicable: false,
                            isSwiggyBlackOtpAnimationEnabled: false,
                            isAffordableM2ExperienceEnabledOnOrderConfirmationScreen: false,
                            orderTransitionScreenNewMsgEnabled: true,
                            successMessageInfo:
                                "You saved [[₹31]] on this order with {{img|v1634558776/swiggy_one/OneLogo_3x.png}}",
                            successMessageInfoV2:
                                "152 saved <br> On Time Delivery <br> Express Delivery{{img|portal/testing/express_logo.png}}",
                            isSwiggyBlackApplicable: false,
                            orderId: 123455,
                            orderStatus: "processing",
                            postStatus: "processing",
                            orderType: "regular",
                            orderTotal: 100,
                            primaryPaymentTransactionAmount: 100,
                            orderItems: [{ quantity: 1 }],
                            juspayMeta: {
                                juspayClientAuthToken: "Xxxx-s-sds",
                                juspayClientAuthTokenExpiry: 1000,
                            },
                            deliveryAddress: {
                                id: "236594699",
                                version: 8,
                                name: "Shriphad Rao",
                                addressLine1: "Budhwal, Haryana 123023, India",
                                addressLine2: "",
                                address: "Budhwal, Haryana 123023, India",
                                landmark: "f",
                                area: "test",
                                mobile: "7989419452",
                                alternateMobile: "",
                                annotation: "work",
                                instructions: "Landmark: f",
                                email: "shriphadnike@gmail.com",
                                flatNo: "123",
                                city: "",
                                lat: "27.8374445",
                                lng: "76.1725774",
                                isVerified: false,
                                addressCategory: 2,
                            },
                            paasId: "123",
                            credConfirmationTimeMinutes: "1",
                            paymentPartnerOrderId: "123455E1",
                            paymentTxnId: "123455E1",
                            successMessageType: "SUPER",
                            confirmationTimeMinutes: "5",
                            rawResponse: {
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                order_data: {
                                    key: "fce9c01a-6fec-4fc6-8c84-1119ccf286c4",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_id: 123455,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_status: "processing",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    post_status: "processing",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    juspay_meta: {
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        juspay_client_auth_token: "Xxxxxx",
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        juspay_client_auth_token_expiry: 1000,
                                    },
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_items: [{ quantity: 1 }],
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_payment_method: params.paymentMethod,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_total: 100,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_type: "regular",
                                },
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                retry_count: 0,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                retry_delay_time_in_ms: 0,
                            },
                            pendingPaymentParams: {
                                method: "POST",
                                endpoint: "/api/v1/confirm",
                                host: "https://chckout.swiggy.com",
                                headers: {},
                            },
                        },
                        retryCount: 0,
                        retryDelayTimeInMs: 0,
                        xpData: {
                            orderTransitionScreenMsg: BOOLEAN_AS_STRING.TRUE,
                        },
                    },
                };
                setTimeout(() => resolve(mockResponse), 2000);
            });
        }
    }

    return await makeApiCall({
        apiHost,
        endpoint: ENDPOINT.PLACE_ORDER,
        bodyParams: params,
        options: { method: "POST" },
    });
}

export async function confirmOrder(apiHost: string, params: ConfirmOrderParams): Promise<ApiResponse<OrderApiData>> {
    if (__DEV__) {
        const mockConfirmOrder = document.cookie.indexOf("mock_confirm_order=1") !== -1;
        if (mockConfirmOrder) {
            return new Promise((resolve) => {
                const mockResponse: ApiResponse<OrderApiData> = {
                    statusCode: 0,
                    data: {
                        orderData: {
                            orderPaymentMethod: "",
                            orderId: 123455,
                            orderType: "regular",
                            orderStatus: "processing",
                            postStatus: "success",
                            orderTotal: 100,
                            orderItems: [{ quantity: 1 }],
                            successMessageInfo:
                                "You saved [[₹31]] on this order with {{img|v1634558776/swiggy_one/OneLogo_3x.png}}",
                            successMessageInfoV2:
                                "[[₹162]] saved <br/> ON-TIME GUARANTEE <br/> EXPRESS DELIVERY {{logo.jpg}}",
                            isSwiggyBlackApplicable: false,
                            isSwiggyBlackOtpApplicable: false,
                            isSwiggyBlackOtpAnimationEnabled: false,
                            isAffordableM2ExperienceEnabledOnOrderConfirmationScreen: false,
                            orderTransitionScreenNewMsgEnabled: true,
                            juspayMeta: {
                                juspayClientAuthToken: "Xxxx-s-sds",
                                juspayClientAuthTokenExpiry: 1000,
                            },
                            paasId: "123",
                            paymentPartnerOrderId: "123455E1",
                            paymentTxnId: "123455E1",
                            successMessageType: "SUPER",
                            deliveryAddress: {
                                id: "236594699",
                                version: 8,
                                name: "Shriphad Rao",
                                addressLine1: "Budhwal, Haryana 123023, India",
                                addressLine2: "",
                                address: "Budhwal, Haryana 123023, India",
                                landmark: "f",
                                area: "test",
                                mobile: "7989419452",
                                alternateMobile: "",
                                annotation: "work",
                                instructions: "Landmark: f",
                                email: "shriphadnike@gmail.com",
                                flatNo: "123",
                                city: "",
                                lat: "27.8374445",
                                lng: "76.1725774",
                                isVerified: false,
                                addressCategory: 2,
                            },
                            rawResponse: {
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                order_data: {
                                    key: "fce9c01a-6fec-4fc6-8c84-1119ccf286c4",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_id: 123455,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_status: "processing",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    post_status: "processing",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    juspay_meta: {
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        juspay_client_auth_token: "Xxxxxx",
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        juspay_client_auth_token_expiry: 1000,
                                    },
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_items: [{ quantity: 1 }],
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_payment_method: "",
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_total: 100,
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    order_type: "regular",
                                },
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                retry_count: 0,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                retry_delay_time_in_ms: 0,
                            },
                            pendingPaymentParams: {
                                method: "POST",
                                endpoint: "/api/v1/confirm",
                                host: "https://chckout.swiggy.com",
                                headers: {},
                            },
                        },
                        retryCount: 0,
                        retryDelayTimeInMs: 0,
                    },
                };
                setTimeout(() => resolve(mockResponse), 2500);
            });
        }
    }

    return await makeApiCall({
        apiHost,
        endpoint: ENDPOINT.CONFIRM_ORDER,
        bodyParams: params,
        options: { method: "POST" },
    });
}
